import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/context/auth/AuthState";
import { useUserProfile } from "../hooks/useUserData";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import {
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Ruler,
  User,
  Weight,
  Calendar,
} from "lucide-react";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatPhone = (phone) => {
  if (!phone) return "—";
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const ProfileField = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3">
    <div className="mt-0.5 rounded-full bg-emerald-50 p-2 text-emerald-700">
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-0.5 break-words text-sm font-medium text-gray-900">
        {value || "—"}
      </p>
    </div>
  </div>
);

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { data: profile, isLoading, error } = useUserProfile(
    currentUser?.user_id
  );

  const user = profile || currentUser || {};
  const firstName = user.first_name || user.firstName || "";
  const lastName = user.last_name || user.lastName || "";
  const fullName = [firstName, user.middle_name, lastName]
    .filter(Boolean)
    .join(" ");
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "U";

  const address = [
    user.locality,
    user.city,
    user.state,
    user.country,
    user.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (isLoading && !currentUser) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
      <Card className="overflow-hidden border-none bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
        <CardContent className="flex items-center gap-4 p-6">
          <Avatar className="h-16 w-16 border-2 border-white/40">
            {user.profile_picture && (
              <AvatarImage src={user.profile_picture} alt={fullName} />
            )}
            <AvatarFallback className="bg-white/20 text-lg font-semibold text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-bold">
              {fullName || "Your Profile"}
            </h1>
            <p className="mt-1 text-sm text-emerald-50">
              {formatPhone(user.phone)}
            </p>
          </div>
        </CardContent>
      </Card>

      {error && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Could not refresh profile details. Showing saved account info.
        </p>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Personal details</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ProfileField icon={User} label="Full name" value={fullName} />
          <Separator />
          <ProfileField icon={Phone} label="Phone" value={formatPhone(user.phone)} />
          <Separator />
          <ProfileField icon={Mail} label="Email" value={user.email} />
          <Separator />
          <ProfileField icon={Calendar} label="Date of birth" value={formatDate(user.dob)} />
          <Separator />
          <ProfileField icon={User} label="Gender" value={user.gender} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Health & location</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ProfileField
            icon={Ruler}
            label="Height"
            value={user.height ? `${user.height} cm` : null}
          />
          <Separator />
          <ProfileField
            icon={Weight}
            label="Weight"
            value={user.weight ? `${user.weight} kg` : null}
          />
          <Separator />
          <ProfileField icon={MapPin} label="Address" value={address} />
        </CardContent>
      </Card>

      <Button
        variant="outline"
        className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
        onClick={handleLogout}
      >
        <LogOut className="mr-2 h-4 w-4" />
        Logout
      </Button>
    </div>
  );
};

export default ProfilePage;
